"use client";

import React, { useState, useEffect } from "react";
import { UploadCloud, CheckCircle2, AlertTriangle, FileText, ChevronRight, Activity, Calendar, MapPin, Search, X, QrCode, CreditCard, Handshake, Sparkles } from "lucide-react";
import dynamic from "next/dynamic";
import { initMercadoPago, Wallet } from "@mercadopago/sdk-react";
import { ModernDatePicker } from "@repo/ui/date-picker";
import { ModalAlert } from "@repo/ui/toast";
import { Skeleton } from "@repo/ui/skeleton";
import { SynaptixLogo } from "@repo/ui/logo";

const mpKey = process.env.NEXT_PUBLIC_MERCADOPAGO_PUBLIC_KEY;
if (mpKey) {
  initMercadoPago(mpKey, { locale: "es-CO" });
}

const LocationPicker = dynamic(() => import("../components/LocationPicker"), { ssr: false });
import PreStudyRecommendationsCard from "../components/PreStudyRecommendationsCard";

type AIStatus = "IDLE" | "PROCESSING" | "SUCCESS" | "REQUIRES_HUMAN_REVIEW" | "FAILED";

export default function Home() {
  const [step, setStep] = useState(1);
  const [modalAlert, setModalAlert] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, title: "", message: "", variant: "info" });

  const showAlert = (title: string, message: string, variant: "info" | "success" | "warning" | "error" = "info") =>
    setModalAlert({ isOpen: true, title, message, variant });
  
  // AI State
  const [aiStatus, setAiStatus] = useState<AIStatus>("IDLE");
  const [pollingTaskId, setPollingTaskId] = useState<string | null>(null);
  
  // Form State
  const [formData, setFormData] = useState({
    patient_name: "",
    species: "",
    breed: "",
    age: "",
    weight: "",
    reproductive_status: "",
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    address: "",
    study_type: "",
    anamnesis: "",
    referring_doctor_name: "",
    referring_doctor_id: "",
    ai_validation_status: "",
    ai_notes: "",
    preferred_date: "",
    preferred_time: "",
    address_details: "",
    clinic_email: "",
    latitude: 0,
    longitude: 0,
  });
  
  // Track fields extracted by AI for UI indication
  const [aiExtractedFields, setAiExtractedFields] = useState<string[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [hasReadRecommendations, setHasReadRecommendations] = useState(false);
  const [showRecommendationsError, setShowRecommendationsError] = useState(false);
  const [showSlotConflictModal, setShowSlotConflictModal] = useState(false);

  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isLocationValid, setIsLocationValid] = useState<boolean>(true);
  const [locationErrorReason, setLocationErrorReason] = useState<string | null>(null);
  const [logisticsContact, setLogisticsContact] = useState<{ phone: string; message: string }>({
    phone: "+57 300 9876543",
    message: "Por favor escribe a nuestro equipo logístico de SYNAPTIX por WhatsApp con tu dirección exacta. Daremos respuesta en un máximo de 12 horas para verificar si podemos programar una visita médica a domicilio en tu zona."
  });
  const [bankSettings, setBankSettings] = useState({
    bank_name: "Davivienda",
    account_type: "Ahorros",
    account_number: "0072 0074 7785",
    account_holder: "1026599528",
    breb_key: "@PLATA3102911633"
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const [b2cPriceCop, setB2cPriceCop] = useState(190000);

  const calculateDistance = (lat1?: number | null, lon1?: number | null, lat2?: number | null, lon2?: number | null) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    const R = 6371e3; // Radio de la tierra en metros
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distancia en metros
  };

  const distFromBase = (formData.latitude && formData.longitude) 
    ? calculateDistance(4.60971, -74.08175, formData.latitude, formData.longitude) 
    : 0;
  const isOutsideRadius = distFromBase > 4000;
  const locationSurcharge = isOutsideRadius ? 15000 : 0;
  const totalB2cPrice = b2cPriceCop + locationSurcharge;

  useEffect(() => {
    fetch("/api/admin/settings/logistics-contact")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.phone) {
          const b2cMsg = data.message && !data.message.toLowerCase().includes("clínica")
            ? data.message
            : "Por favor escribe a nuestro equipo logístico de SYNAPTIX por WhatsApp con tu dirección exacta. Daremos respuesta en un máximo de 12 horas para verificar si podemos programar una visita médica a domicilio en tu zona.";
          setLogisticsContact({ phone: data.phone, message: b2cMsg });
        }
      })
      .catch((e) => console.error("Could not fetch logistics contact", e));

    fetch("/api/admin/settings/bank-transfer")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setBankSettings(data);
      })
      .catch((e) => console.error("Could not fetch bank transfer settings", e));

    fetch("/api/studies/pricing")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.b2c_price_cop) setB2cPriceCop(Number(data.b2c_price_cop));
      })
      .catch((e) => console.error("Could not fetch studies pricing", e));
  }, []);

  // Payment & Submit State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [transferReference, setTransferReference] = useState("");
  const [loadingMP, setLoadingMP] = useState(false);
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [createdAppointmentId, setCreatedAppointmentId] = useState<string | null>(null);

  // Check URL for Payment Success
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("payment_success") === "true") {
        setSuccessMessage(true);
        // Opcional: limpiar la URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Polling para verificar si el webhook ya confirmó la cita B2C
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showPaymentModal && createdAppointmentId) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`/api/b2c/payments/verify/${createdAppointmentId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.status === "verified and registered") {
              setShowPaymentModal(false);
              setSuccessMessage(true);
              setTimeout(() => {
                window.location.reload();
              }, 5000);
            }
          }
        } catch (e) {}
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [showPaymentModal, createdAppointmentId]);

  // Polling Logic de la IA
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (pollingTaskId && aiStatus === "PROCESSING") {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`/api/b2b/dashboard/requests/referral-status/${pollingTaskId}`);
          if (!res.ok) return;
          const data = await res.json();
          
          if (data.status === "SUCCESS") {
             setAiStatus("SUCCESS");
             const result = data.result;
             
             setFormData(prev => {
               let finalAnamnesis = result.diagnostico_anamnesis || prev.anamnesis;
               if (result.notas_adicionales) {
                   finalAnamnesis += `\n\n[NOTAS DE LA IA]: ${result.notas_adicionales}`;
               }
               
               const newFields = {
                 patient_name: result.nombre_paciente || prev.patient_name,
                 species: result.especie || prev.species,
                 breed: result.raza || prev.breed,
                 age: result.edad || prev.age,
                 weight: result.peso || prev.weight,
                 reproductive_status: result.estado_reproductivo || prev.reproductive_status,
                 owner_name: result.nombre_propietario || prev.owner_name,
                 owner_phone: result.telefono_propietario || prev.owner_phone,
                 owner_email: result.correo_propietario || prev.owner_email,
                 clinic_email: result.correo_veterinaria || prev.clinic_email,
                 address: result.direccion || prev.address,
                 study_type: result.tipo_estudio || prev.study_type,
                 anamnesis: finalAnamnesis,
                 referring_doctor_name: result.nombre_veterinario_remitente || prev.referring_doctor_name,
                 referring_doctor_id: result.tarjeta_profesional || prev.referring_doctor_id,
                 ai_validation_status: "PASSED"
               };
               
               // Track which fields were populated by AI
               const extracted = [];
               if (result.nombre_paciente) extracted.push("patient_name");
               if (result.especie) extracted.push("species");
               if (result.raza) extracted.push("breed");
               if (result.edad) extracted.push("age");
               if (result.peso) extracted.push("weight");
               if (result.estado_reproductivo) extracted.push("reproductive_status");
               if (result.tipo_estudio) extracted.push("study_type");
               setAiExtractedFields(extracted);
               
               return { ...prev, ...newFields };
             });
             clearInterval(interval);
             
             // Auto-proceed to the next step after a short delay to show the success message
             setTimeout(() => {
               setStep(2);
               window.scrollTo({ top: 0, behavior: "smooth" });
             }, 1500);
          } else if (data.status === "REQUIRES_HUMAN_REVIEW") {
             setAiStatus("REQUIRES_HUMAN_REVIEW");
             
             let customError = "El documento no cumple con nuestras reglas de seguridad médica.";
             if (data.result) {
               const missing = [];
               if (!data.result.nombre_veterinario_remitente) missing.push("Nombre del Médico");
               if (!data.result.tarjeta_profesional) missing.push("Tarjeta Profesional (TP)");
               if (!data.result.tipo_estudio) missing.push("Estudio Válido (Ecografía Abdominal, Gestación o Cuello)");
               
               if (data.result.notas_adicionales && data.result.notas_adicionales.includes("Error:")) {
                 customError = data.result.notas_adicionales;
               } else if (missing.length > 0) {
                 customError = `Falta información crítica: ${missing.join(", ")}. Por favor, sube una foto donde la firma, tarjeta profesional y el estudio sean claramente legibles.`;
               } else if (data.result.notas_adicionales) {
                 customError = data.result.notas_adicionales;
               }
             }

             setFormData(prev => ({
               ...prev,
               ai_validation_status: "REQUIRES_HUMAN_REVIEW",
               ai_notes: customError
             }));
             clearInterval(interval);
          } else if (data.status === "FAILED") {
             setAiStatus("FAILED");
             clearInterval(interval);
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [pollingTaskId, aiStatus]);

  const [isDragging, setIsDragging] = useState(false);

  const uploadFile = async (file: File) => {
    if (!file) return;
    
    const formDataObj = new FormData();
    formDataObj.append("file", file);
    
    setAiStatus("PROCESSING");

    try {
      const res = await fetch("/api/b2c/upload-remision", {
        method: "POST",
        body: formDataObj
      });
      const data = await res.json();
      if (data.task_id) {
        setPollingTaskId(data.task_id);
      } else {
        setAiStatus("FAILED");
      }
    } catch (e) {
      console.error("Error uploading referral:", e);
      setAiStatus("FAILED");
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const fetchAvailability = async (dateStr: string, lat: number, lng: number) => {
    if (!dateStr || lat === 0 || lng === 0) return;
    setLoadingSlots(true);
    setAvailableSlots([]);
    setIsLocationValid(true);
    setLocationErrorReason(null);
    try {
      const res = await fetch(`/api/b2b/availability?date=${dateStr}&lat=${lat}&lng=${lng}`);
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.available_slots || []);
        if (data.is_valid_location === false) {
          setIsLocationValid(false);
          setLocationErrorReason(data.error_reason || "Ubicación fuera de cobertura.");
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleAddressSearch = async (addressQuery: string) => {
    if (!addressQuery.trim()) return;
    try {
      const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;
      if (!GOOGLE_MAPS_KEY) return;
      
      let cleanQuery = addressQuery
        .replace(/\s+[nN]o?\.?\s+/g, ' # ') 
        .replace(/(\d)\s+([a-zA-Z])\b/g, '$1$2'); 

      const finalQuery = cleanQuery.toLowerCase().includes("bogot") 
        ? cleanQuery 
        : `${cleanQuery}, Bogotá, Colombia`;
        
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(finalQuery)}&components=country:CO&key=${GOOGLE_MAPS_KEY}`);
      if (res.ok) {
        const data = await res.json();
        if (data.results && data.results.length > 0) {
          const lat = data.results[0].geometry.location.lat;
          const lng = data.results[0].geometry.location.lng;
          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
          setAvailableSlots([]);
          if (formData.preferred_date) {
            fetchAvailability(formData.preferred_date, lat, lng);
          }
        }
      }
    } catch (e) {
      console.error("Error geocoding address:", e);
    }
  };

  const handleLocationSelect = async (lat: number, lng: number, address?: string) => {
    let finalAddress = address;
    
    if (!finalAddress) {
      try {
        const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;
        if (GOOGLE_MAPS_KEY) {
          const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_KEY}`);
          if (res.ok) {
            const data = await res.json();
            if (data.results && data.results.length > 0) {
              finalAddress = data.results[0].formatted_address;
            }
          }
        }
      } catch (e) {
        console.error("Error reverse geocoding:", e);
      }
    }

    setFormData(prev => ({ 
      ...prev, 
      latitude: lat, 
      longitude: lng,
      ...(finalAddress ? { address: finalAddress } : {})
    }));
    setAvailableSlots([]);
    if (formData.preferred_date) {
      fetchAvailability(formData.preferred_date, lat, lng);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "preferred_date") {
      setFormData(prev => ({ ...prev, preferred_time: "" }));
      if (formData.latitude && formData.longitude) {
        fetchAvailability(value, formData.latitude, formData.longitude);
      }
    }
  };

  const nextStep = () => {
    if (step === 2) {
      if (!formData.patient_name || !formData.species || !formData.breed || !formData.age || !formData.weight || !formData.reproductive_status) {
        showAlert("Datos del paciente requeridos", "Por favor completa todos los datos obligatorios del paciente antes de continuar.", "warning");
        return;
      }
    }
    setStep(s => {
      const next = Math.min(s + 1, 3);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return next;
    });
  };
  const prevStep = () => {
    setStep(s => {
      const prev = Math.max(s - 1, 1);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return prev;
    });
  };

  const handleConfirmClick = async () => {
    // Basic validation
    if (!formData.preferred_date || !formData.preferred_time || !formData.owner_name || !formData.owner_phone || !formData.address) {
      showAlert("Campos obligatorios incompletos", "Por favor completa todos los campos obligatorios de la cita para poder continuar.", "warning");
      return;
    }
    if (!hasReadRecommendations) {
      setShowRecommendationsError(true);
      setTimeout(() => setShowRecommendationsError(false), 3500);
      return;
    }
    
    setIsSubmitting(true);
    setLoadingMP(true);
    
    // Fake delay for UI
    setIsConfirming(true);
    setConfirmText("Confirmando disponibilidad...");
    
    try {
      // Create draft for MP
      const resAppointment = await fetch("/api/b2c/appointments/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ...formData,
            payment_method: "MERCADOPAGO"
        })
      });
      if (resAppointment.ok) {
        const appointmentData = await resAppointment.json();
        setCreatedAppointmentId(appointmentData.id);
        
        // Wait at least 3 seconds total for the fake delay
        await new Promise(r => setTimeout(r, 2000));
        setConfirmText("Disponibilidad confirmada, por favor realice el pago correspondiente para finalizar el agendamiento.");
        await new Promise(r => setTimeout(r, 2000));
        
        setIsConfirming(false);
        setShowPaymentModal(true);
        
        // Request preference
        const resPreference = await fetch("/api/b2c/payments/create-preference", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointment_id: appointmentData.id,
            amount: totalB2cPrice
          })
        });
        if (resPreference.ok) {
          const prefData = await resPreference.json();
          setPreferenceId(prefData.preferenceId);
        }
      } else if (resAppointment.status === 409) {
        setShowSlotConflictModal(true);
        if (formData.preferred_date && formData.latitude && formData.longitude) {
            fetchAvailability(formData.preferred_date, formData.latitude, formData.longitude);
        }
      } else {
        showAlert("Error al agendar", "Ocurrió un error al intentar agendar la cita. Por favor intenta nuevamente.", "error");
      }
    } catch(e) {
      console.error("Error iniciando pasarela", e);
    } finally {
      setLoadingMP(false);
      setIsSubmitting(false);
      setIsConfirming(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!transferReference) {
      showAlert("Referencia requerida", "Por favor ingresa el nombre de quien transfiere o el # de comprobante.", "warning");
      return;
    }
    setIsSubmitting(true);
    try {
      if (createdAppointmentId) {
        // Adjuntar comprobante a la cita que fue reservada al abrir la pasarela
        const res = await fetch("/api/b2c/appointments/attach-manual-payment", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            appointment_id: createdAppointmentId,
            payment_reference: transferReference
          })
        });
        if (res.ok) {
          setShowPaymentModal(false);
          setSuccessMessage(true);
        } else {
          const err = await res.json();
          showAlert("Error en comprobante", "Error al adjuntar comprobante: " + (err.detail || "Intenta nuevamente."), "error");
        }
      } else {
        // Fallback por si la cita no se había creado previamente
        const res = await fetch("/api/b2c/appointments/create", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
              ...formData,
              payment_method: "MANUAL",
              payment_reference: transferReference
          })
        });
        if (res.ok) {
          setShowPaymentModal(false);
          setSuccessMessage(true);
        } else if (res.status === 409) {
          setShowSlotConflictModal(true);
          if (formData.preferred_date && formData.latitude && formData.longitude) {
              fetchAvailability(formData.preferred_date, formData.latitude, formData.longitude);
          }
        } else {
          const err = await res.json();
          showAlert("Error al agendar", "Error al agendar: " + (err.detail || "Intenta nuevamente."), "error");
        }
      }
    } catch (e) {
      console.error(e);
      showAlert("Error de conexión", "Error de conexión al servidor. Por favor verifica tu red e intenta nuevamente.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (successMessage) {
    return (
      <main className="min-h-screen bg-[#060B14] text-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] pointer-events-none" />
        <div className="text-center z-10 animate-in zoom-in duration-500 flex flex-col items-center">
          <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 border border-emerald-500/30">
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-4">¡Cita Confirmada!</h1>
          <p className="text-lg text-slate-400 max-w-md mb-4">
            Hemos recibido tu solicitud y la Dra. Angie Cruz ha sido notificada. Verificaremos tu pago en breve y el doctor llegará en la fecha y hora acordada.
          </p>

          <div className="w-full max-w-2xl mb-8">
            <PreStudyRecommendationsCard readOnly={true} variant="reminder" />
          </div>

          <button onClick={() => window.location.reload()} className="px-8 py-3 bg-slate-800 hover:bg-slate-700 rounded-full font-semibold transition-colors">Agendar otra cita</button>
        </div>
      </main>
    );
  }

  return (
    <main className={step === 1 ? "h-screen max-h-screen overflow-hidden bg-[#060B14] text-white flex flex-col justify-between py-5 px-4 md:px-8 relative" : "min-h-screen bg-[#060B14] text-white flex flex-col items-center py-8 px-4 relative"}>
      {/* Background Orbs for Glassmorphism effect */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[150px] pointer-events-none" />

      <header className={`${step === 1 ? 'mb-3' : 'mb-8'} text-center z-10 flex flex-col items-center`}>
        <div className="flex items-center justify-center mb-2">
          <SynaptixLogo size={step === 1 ? 40 : 50} showText={true} />
        </div>
        <p className={`${step === 1 ? 'text-sm md:text-base' : 'text-lg'} text-slate-400 max-w-xl mx-auto`}>
          Agenda tu ecografía veterinaria a domicilio en menos de 2 minutos. Sube tu remisión y nuestra IA hará el resto.
        </p>
      </header>

      {/* Steps Indicator */}
      <div className={`w-full ${step === 1 ? 'max-w-2xl mb-4' : 'max-w-4xl mb-8'} mx-auto flex justify-between items-center z-10 px-6 md:px-12 relative`}>
        <div className="absolute left-10 right-10 top-1/2 h-0.5 bg-slate-800 -z-10 rounded-full" />
        <div className={`absolute left-10 top-1/2 h-0.5 bg-cyan-500 -z-10 transition-all duration-500 ease-out`} style={{ width: `${((step - 1) / 2) * 80}%` }} />
        
        {[ 
          { num: 1, icon: FileText, title: "Remisión" },
          { num: 2, icon: Activity, title: "Datos Clínicos" },
          { num: 3, icon: Calendar, title: "Cita" }
        ].map((s) => (
          <div key={s.num} className="flex flex-col items-center gap-2">
            <div className={`${step === 1 ? 'w-10 h-10' : 'w-12 h-12'} rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
              step >= s.num ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)]' : 'bg-slate-900 border-slate-800 text-slate-500'
            }`}>
              <s.icon className="w-4 h-4" />
            </div>
            <span className={`text-[11px] font-semibold uppercase tracking-wider ${step >= s.num ? 'text-cyan-400' : 'text-slate-500'}`}>{s.title}</span>
          </div>
        ))}
      </div>

      {/* Main Wizard Card */}
      <div className={`w-full ${step === 1 ? 'max-w-3xl mx-auto p-6 md:p-8 flex-1 flex flex-col justify-center max-h-[440px]' : 'max-w-4xl p-8 md:p-12'} bg-slate-900/50 backdrop-blur-xl border border-slate-800/60 rounded-3xl shadow-2xl z-10 relative`}>
        
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col justify-center h-full">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-1">1. Sube tu Remisión Médica</h2>
            <p className="text-xs md:text-sm text-slate-400 mb-5">Nuestra Inteligencia Artificial leerá los datos automáticamente para ahorrarte tiempo.</p>

            <div className="relative group cursor-pointer w-full">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative w-full min-h-[260px] border-2 border-dashed rounded-2xl p-6 md:p-8 flex flex-col items-center justify-center transition-all duration-300 ${
                  isDragging ? 'border-cyan-400 bg-cyan-900/40 scale-[1.02]' :
                  aiStatus === 'IDLE' ? 'border-cyan-500/50 bg-cyan-950/20 hover:border-cyan-400 hover:bg-cyan-900/30' :
                  aiStatus === 'PROCESSING' ? 'border-blue-500/50 bg-blue-950/20' :
                  aiStatus === 'SUCCESS' ? 'border-emerald-500/50 bg-emerald-950/20' :
                  'border-red-500/50 bg-red-950/20'
                }`}
              >
                <input 
                  type="file" 
                  accept="image/*,.pdf" 
                  onChange={handleFileInputChange} 
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  disabled={aiStatus === "PROCESSING"}
                />

                {aiStatus === "IDLE" && (
                  <>
                    <UploadCloud className="w-12 h-12 text-cyan-400 mb-3 group-hover:scale-110 transition-transform duration-300" />
                    <h3 className="text-base md:text-lg font-semibold text-white mb-1">Haz clic para subir o arrastra la foto de tu remisión</h3>
                    <p className="text-xs text-slate-400 text-center">Formatos soportados: JPG, PNG, PDF. Max 10MB.</p>
                  </>
                )}

                {aiStatus === "PROCESSING" && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                    <h3 className="text-base md:text-lg font-semibold text-cyan-400 animate-pulse">Auditando remisión con IA...</h3>
                    <p className="text-xs text-slate-400">Verificando firma médica y extrayendo datos clínicos.</p>
                  </div>
                )}

                {aiStatus === "SUCCESS" && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                      <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-emerald-400">¡Validación Exitosa!</h3>
                    <p className="text-xs text-slate-400">Hemos extraído todos los datos de {formData.patient_name || 'tu mascota'}.</p>
                  </div>
                )}

                {aiStatus === "REQUIRES_HUMAN_REVIEW" && (
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-base font-semibold text-red-400">Documento Rechazado</h3>
                    <p className="text-xs font-medium text-red-300 max-w-md">{formData.ai_notes}</p>
                    <p className="text-[11px] text-slate-400 mt-1">Por favor sube una imagen diferente o ponte en contacto con soporte.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-8 duration-500">
            <h2 className="text-2xl font-bold text-white mb-2">2. Verifica los Datos Clínicos</h2>
            <p className="text-slate-400 mb-8">Revisa la información extraída y completa lo que haga falta.</p>

            {aiStatus === "PROCESSING" && (
              <div className="mb-6 p-6 rounded-2xl bg-[#06101E]/90 border border-synaptix-cyan/40 flex flex-col gap-4 animate-pulse shadow-[0_0_25px_rgba(0,229,255,0.1)]">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-synaptix-cyan border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <p className="text-xs md:text-sm text-cyan-300 font-medium">
                    La Inteligencia Artificial de SYNAPTIX está analizando tu remisión médica en segundo plano...
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Skeleton className="h-11 w-full rounded-lg bg-slate-800/60" />
                  <Skeleton className="h-11 w-full rounded-lg bg-slate-800/60" />
                  <Skeleton className="h-11 w-full rounded-lg bg-slate-800/60" />
                  <Skeleton className="h-11 w-full rounded-lg bg-slate-800/60" />
                </div>
              </div>
            )}

            {aiStatus === "SUCCESS" && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/40 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <p className="text-xs md:text-sm text-emerald-300 font-medium">
                  ¡Remisión leída exitosamente por la IA! Revisa y confirma los campos resaltados con nuestra validación automática.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Nombre del Paciente {aiExtractedFields.includes("patient_name") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <input name="patient_name" value={formData.patient_name} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("patient_name") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`} placeholder="Ej. Toby" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Especie {aiExtractedFields.includes("species") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <select name="species" value={formData.species} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("species") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`}>
                  <option value="">Seleccionar...</option>
                  <option value="Canino">Canino</option>
                  <option value="Felino">Felino</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Raza {aiExtractedFields.includes("breed") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <input name="breed" value={formData.breed} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("breed") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`} placeholder="Ej. Poodle" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Edad {aiExtractedFields.includes("age") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <input name="age" value={formData.age} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("age") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`} placeholder="Ej. 5 años" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Peso {aiExtractedFields.includes("weight") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <input name="weight" value={formData.weight} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("weight") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`} placeholder="Ej. 12 kg" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Estado Reproductivo {aiExtractedFields.includes("reproductive_status") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <select name="reproductive_status" value={formData.reproductive_status} onChange={handleChange} className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("reproductive_status") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all`}>
                  <option value="">Seleccionar...</option>
                  <option value="Entero">Entero</option>
                  <option value="Esterilizado">Esterilizado</option>
                </select>
              </div>
              
              {/* Resumen de Solo Lectura (Gatekeeper Antifraude) */}
              <div className="md:col-span-2 mt-4">
                <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-xl p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-bold text-sm tracking-wide">AVAL MÉDICO CONFIRMADO (SOLO LECTURA)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 block mb-1">Médico Remitente:</span>
                      <span className="text-slate-200 font-medium">{formData.referring_doctor_name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">Tarjeta Profesional (TP):</span>
                      <span className="text-slate-200 font-medium">{formData.referring_doctor_id || 'N/A'}</span>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-slate-500 block mb-1">Correo de la Veterinaria (Si lo hay):</span>
                      <span className="text-slate-200 font-medium">{formData.clinic_email || 'N/A'}</span>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-slate-500 block mb-1">Diagnóstico / Anamnesis:</span>
                      <p className="text-slate-200 bg-slate-950/50 p-3 rounded-lg border border-slate-800/50 whitespace-pre-wrap leading-relaxed">{formData.anamnesis || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-10">
              <button onClick={prevStep} className="w-full sm:w-auto h-12 px-6 text-slate-400 hover:text-white font-medium rounded-xl border border-slate-800/60 hover:bg-slate-900 transition-colors">Atrás</button>
              <button onClick={nextStep} className="group w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold h-12 px-8 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)]">
                <span>Siguiente</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-8 duration-500">
            <h2 className="text-2xl font-bold text-white mb-2">3. Agendamiento y Logística</h2>
            <p className="text-slate-400 mb-8">Indícanos dónde y cuándo realizaremos la ecografía.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  Tipo de Estudio Solicitado {aiExtractedFields.includes("study_type") && <Sparkles className="w-3.5 h-3.5 text-emerald-400 inline shrink-0 ml-1" />}
                </label>
                <select 
                  name="study_type" 
                  value={formData.study_type} 
                  onChange={handleChange} 
                  disabled={true}
                  className={`h-11 md:h-12 w-full bg-[#06101E]/80 border ${aiExtractedFields.includes("study_type") ? 'border-emerald-500/60' : 'border-synaptix-cyan/20'} rounded-lg px-4 text-slate-400 focus:outline-none transition-colors cursor-not-allowed`}
                >
                  <option value="">Seleccionar...</option>
                  <option value="Ecografía Abdominal">Ecografía Abdominal</option>
                  <option value="Ecografía de Gestación">Ecografía de Gestación</option>
                  <option value="Ecografía de Cuello">Ecografía de Cuello</option>
                </select>
                <span className="text-xs text-slate-500 mt-1">Este campo es inmodificable por seguridad médica.</span>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Dirección de Domicilio (GPS)</label>
                <div className="bg-slate-950/30 p-2 rounded-xl border border-slate-800/50 mb-4 mt-2">
                  <p className="text-xs text-slate-500 mb-2 px-2">Usa el GPS para ubicar tu posición o arrastra el pin en el mapa.</p>
                  <LocationPicker 
                    initialPosition={formData.latitude ? [formData.latitude, formData.longitude] : [4.60971, -74.08175]}
                    onLocationSelect={handleLocationSelect} 
                    className="h-[300px] w-full rounded-lg border border-slate-700/50 relative"
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input name="address" value={formData.address} onChange={handleChange} className="h-11 md:h-12 w-full bg-[#06101E]/80 border border-synaptix-cyan/20 rounded-lg pl-12 pr-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all" placeholder="Ej. Calle 123 #45-67, Bogotá" />
                  </div>
                  <button 
                    type="button"
                    onClick={() => handleAddressSearch(formData.address)}
                    className="h-11 md:h-12 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Validar Cobertura
                  </button>
                </div>
                {isOutsideRadius && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mt-2 flex items-center justify-between text-xs shadow-md animate-in fade-in duration-300">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400 text-base">📍</span>
                      <div>
                        <p className="font-bold text-amber-400">Ubicación fuera del radio de 4 km</p>
                        <p className="text-slate-300">Aplica un recargo por desplazamiento de +$15.000 COP incluido en el total.</p>
                      </div>
                    </div>
                    <span className="font-bold text-amber-400 shrink-0 ml-3">+$15.000 COP</span>
                  </div>
                )}
                <div className="mt-2 flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Detalles Adicionales</label>
                  <input name="address_details" value={formData.address_details} onChange={handleChange} className="h-11 md:h-12 w-full bg-[#06101E]/80 border border-synaptix-cyan/20 rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all" placeholder="Apto, Torre, Conjunto..." />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha de la Cita</label>
                <ModernDatePicker 
                  value={formData.preferred_date} 
                  onChange={(dateStr) => {
                    setFormData(prev => ({ ...prev, preferred_date: dateStr }));
                    if (formData.latitude && formData.longitude) {
                      fetchAvailability(dateStr, formData.latitude, formData.longitude);
                    }
                  }} 
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Hora Preferida</label>
                {!formData.latitude || !formData.longitude ? (
                  <div className="text-sm text-amber-400 mt-2 bg-amber-400/10 p-3 rounded-lg border border-amber-400/20 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>Por favor, ubica primero tu dirección en el mapa para calcular tiempos de ruta.</span>
                  </div>
                ) : loadingSlots ? (
                  <div className="text-sm text-cyan-400 mt-2 flex items-center gap-2"><Activity className="w-4 h-4 animate-spin"/> Calculando disponibilidad...</div>
                ) : !formData.preferred_date ? (
                  <div className="text-sm text-slate-400 mt-2 italic">Selecciona una fecha primero.</div>
                ) : !isLocationValid ? (
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-cyan-950/80 via-[#0A1424] to-slate-900 border border-cyan-500/50 text-left flex flex-col gap-4 my-3 shadow-[0_0_35px_rgba(6,182,212,0.25)] animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-400/50 flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)] shrink-0">
                        <Activity className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <h4 className="text-base font-bold text-cyan-300">
                          No estamos disponibles por el momento en esta zona de la ciudad
                        </h4>
                        <p className="text-xs text-slate-300 leading-relaxed font-medium">
                          ¡Muy pronto lo estaremos, espéranos! Estamos expandiendo activamente la cobertura de nuestra flota de ecografía a domicilio.
                        </p>
                      </div>
                    </div>

                    <div className="bg-cyan-900/30 border border-cyan-500/30 rounded-xl p-4 flex flex-col gap-2.5">
                      <div className="flex items-center gap-2 text-cyan-300 font-bold text-xs">
                        <FileText className="w-4 h-4 text-cyan-300 shrink-0 inline mr-1" />
                        <span>¿Requieres una ecografía extraordinaria en esta ubicación?</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed">
                        {logisticsContact.message}
                      </p>
                      <div className="pt-1">
                        <a
                          href={`https://wa.me/${logisticsContact.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola equipo de Logística SYNAPTIX, soy tutor(a) de un paciente y necesito consultar la viabilidad de una ecografía a domicilio fuera de zona habitual en mi dirección: ${formData.address || "No especificada"}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 rounded-xl text-xs transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                        >
                          <span>WhatsApp Logística ({logisticsContact.phone})</span>
                        </a>
                      </div>
                    </div>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-sm text-red-400 mt-2">No hay horarios disponibles para esta fecha.</div>
                ) : (
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {availableSlots.map(time => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, preferred_time: time }))}
                        className={`px-2 py-2 text-xs rounded-lg font-medium transition-all ${
                          formData.preferred_time === time 
                          ? 'bg-cyan-500 text-slate-950 shadow-[0_0_10px_rgba(6,182,212,0.5)]'
                          : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tu Nombre</label>
                <input name="owner_name" value={formData.owner_name} onChange={handleChange} className="h-11 md:h-12 w-full bg-[#06101E]/80 border border-synaptix-cyan/20 rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all" placeholder="Nombre completo" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tu Teléfono</label>
                <input type="tel" name="owner_phone" value={formData.owner_phone} onChange={handleChange} className="h-11 md:h-12 w-full bg-[#06101E]/80 border border-synaptix-cyan/20 rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all" placeholder="Número celular" />
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tu Correo Electrónico</label>
                <input type="email" name="owner_email" value={formData.owner_email} onChange={handleChange} className="h-11 md:h-12 w-full bg-[#06101E]/80 border border-synaptix-cyan/20 rounded-lg px-4 text-white placeholder-slate-500 focus-visible:ring-2 focus-visible:ring-synaptix-cyan focus-visible:outline-none transition-all" placeholder="tucorreo@ejemplo.com" />
              </div>
            </div>

            {/* Desglose Transparente de Tarifas en COP */}
            <div className="mt-6 mb-8 bg-[#06101E]/90 border border-synaptix-cyan/30 rounded-2xl p-6 shadow-[0_0_25px_rgba(0,229,255,0.07)]">
              <h3 className="text-sm font-bold uppercase tracking-wider text-synaptix-cyan mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-synaptix-cyan" />
                <span>Desglose de Tarifas del Servicio Domiciliario</span>
              </h3>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex justify-between items-center pb-2 border-b border-slate-800">
                  <span>Estudio Ecográfico Domiciliario</span>
                  <span className="font-semibold text-white">${b2cPriceCop.toLocaleString('es-CO')} COP</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-800">
                  <span>Honorarios de Especialista y Lectura IA</span>
                  <span className="text-emerald-400 font-semibold">Incluidos</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-800">
                  <span>Recargo por Desplazamiento Domiciliario {isOutsideRadius ? '(> 4 km)' : ''}</span>
                  <span className={isOutsideRadius ? "text-amber-400 font-bold" : "text-emerald-400 font-semibold"}>
                    {isOutsideRadius ? `+$${locationSurcharge.toLocaleString('es-CO')} COP` : '$0 COP'}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 font-extrabold text-base text-white">
                  <span>Total Neto a Pagar</span>
                  <span className="text-synaptix-cyan text-lg">${totalB2cPrice.toLocaleString('es-CO')} COP</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Tarifa garantizada sin costos ocultos. Puedes pagar con transferencia sin comisiones o en línea con MercadoPago.</span>
              </p>
            </div>

            <div className="mt-8 mb-6">
              <PreStudyRecommendationsCard
                checked={hasReadRecommendations}
                onChange={setHasReadRecommendations}
                showErrorShake={showRecommendationsError}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-10">
              <button onClick={prevStep} className="w-full sm:w-auto h-12 px-6 text-slate-400 hover:text-white font-medium rounded-xl border border-slate-800/60 hover:bg-slate-900 transition-colors">Atrás</button>
              <button 
                onClick={handleConfirmClick} 
                className={`w-full sm:w-auto h-12 px-8 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                  hasReadRecommendations
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 shadow-[0_0_25px_rgba(16,185,129,0.4)] hover:shadow-[0_0_35px_rgba(16,185,129,0.6)] cursor-pointer"
                    : "bg-emerald-500/30 text-white/50 border border-emerald-500/20 cursor-not-allowed"
                }`}
              >
                <span>Confirmar Cita y Pagar</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirming Overlay */}
      {isConfirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#040B16]/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="flex flex-col items-center max-w-md text-center p-8">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4 animate-pulse">
              {confirmText.includes('confirmada') ? '¡Disponibilidad Confirmada!' : 'Confirmando Disponibilidad...'}
            </h2>
            <p className="text-slate-400 text-lg">
              {confirmText.includes('confirmada') 
                ? 'Hemos reservado el espacio en la agenda de tu médico asignado. Por favor, realiza el pago en la siguiente pantalla para finalizar.'
                : 'Estamos verificando en tiempo real la ruta óptima de nuestros especialistas.'}
            </p>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#0b1324] border border-slate-800 rounded-3xl p-6 md:p-10 max-w-4xl w-full relative shadow-2xl overflow-y-auto max-h-[90vh]">
            <button 
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-6 right-6 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors border border-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h2 className="text-3xl font-bold text-white mb-2">Opciones de Pago</h2>
            <p className="text-slate-400 mb-8 max-w-2xl">
              El valor total de tu ecografía es de <span className="text-white font-bold">${totalB2cPrice.toLocaleString('es-CO')} COP</span>. Selecciona el método de pago que prefieras. Recomendamos transferencia para evitar comisiones de pasarela.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Transferencia Directa */}
              <div className="relative bg-[#060b14] border-2 border-cyan-500 rounded-3xl p-6 md:p-8 flex flex-col shadow-[0_0_30px_rgba(6,182,212,0.15)]">
                <div className="absolute -top-4 right-6 bg-cyan-500 text-[#040B16] font-bold text-xs px-4 py-1.5 rounded-full tracking-wider">
                  RECOMENDADO
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">Transferencia Directa</h3>
                <div className="inline-flex items-center px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-semibold mb-6 w-fit">
                  0% COMISIÓN BANCARIA
                </div>
                
                <div className="flex flex-col md:flex-row gap-6 mb-8 flex-1">
                  <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4 justify-center">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">BANCO</p>
                      <p className="font-bold text-white text-lg">{bankSettings.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">TIPO DE CUENTA</p>
                      <p className="font-medium text-slate-200">{bankSettings.account_type}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">NÚMERO DE CUENTA</p>
                      <p className="font-bold text-cyan-400 text-xl tracking-widest whitespace-nowrap">{bankSettings.account_number}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">CC (TITULAR)</p>
                      <p className="font-medium text-slate-200">{bankSettings.account_holder}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center gap-3 w-full md:w-32 shrink-0">
                    <div className="bg-white p-2 rounded-xl w-full aspect-square flex items-center justify-center overflow-hidden">
                      <img src="/api/settings/qr-image" alt="QR Transf" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(bankSettings.bank_name + ':' + bankSettings.account_number)}`; }} />
                    </div>
                    <div className="bg-[#E20B15] text-white font-bold text-[11px] text-center px-2 py-2 rounded-xl w-full uppercase leading-tight shadow-md">
                      Llave Bre-B<br/>{bankSettings.breb_key}
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto">
                  {!showManualForm ? (
                    <button 
                      onClick={() => setShowManualForm(true)}
                      className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 text-[#040B16] font-black text-xl rounded-2xl transition-all shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] flex justify-center items-center gap-2"
                    >
                      ¡YA TRANSFERÍ!
                    </button>
                  ) : (
                    <div className="bg-black/50 p-4 rounded-xl border border-cyan-500/50 animate-in fade-in zoom-in duration-300">
                      <label className="block text-cyan-500 text-xs font-semibold mb-2">
                        Nombre de quien transfiere o # de Comprobante
                      </label>
                      <input 
                        type="text" 
                        value={transferReference}
                        onChange={(e) => setTransferReference(e.target.value)}
                        placeholder="Ej: Clinica Vida o Ref 12345"
                        className="w-full bg-[#0A1424] border border-slate-700 text-white rounded-lg p-3 mb-3 focus:border-cyan-500 outline-none"
                      />
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setShowManualForm(false)}
                          className="px-4 py-2 text-slate-400 hover:text-white text-sm"
                        >
                          Cancelar
                        </button>
                        <button 
                          onClick={handlePaymentSubmit}
                          disabled={isSubmitting || !transferReference.trim()}
                          className="flex-1 bg-emerald-500 text-white font-bold py-2 rounded-lg hover:bg-emerald-400 disabled:opacity-50"
                        >
                          {isSubmitting ? "Enviando..." : "Enviar Comprobante"}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 text-center">
                    <p className="text-xs text-cyan-500 font-semibold mb-1">Si necesitas aprobación urgente, escribe al WhatsApp:</p>
                    <div className="bg-slate-900 text-white font-bold py-2 px-4 rounded-lg inline-block w-full border border-slate-800 text-center">
                      {logisticsContact.phone}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pago en Línea */}
              <div className="bg-[#060b14] border border-slate-800 rounded-3xl p-6 md:p-8 flex flex-col">
                <h3 className="text-2xl font-bold text-white mb-2">Pago en Línea</h3>
                <div className="inline-flex items-center px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm font-semibold mb-6 w-fit">
                  +3.55% Recargo de Pasarela
                </div>
                
                <div className="flex gap-2 mb-6">
                  <div className="bg-slate-800/50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300">PSE</div>
                  <div className="bg-slate-800/50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300">Visa</div>
                  <div className="bg-slate-800/50 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-300">Mastercard</div>
                </div>
                
                <p className="text-slate-400 text-sm leading-relaxed mb-auto">
                  El operador (MercadoPago) cobra una comisión por transacción. Este costo (3.55%) será sumado a tu factura total.
                </p>
                
                <div className="mt-8 pt-6 min-h-[60px] flex flex-col justify-end">
                  {loadingMP ? (
                    <div className="w-full text-center py-4 bg-slate-800/50 rounded-xl text-slate-400 text-sm font-bold animate-pulse">
                      Cargando Pasarela...
                    </div>
                  ) : preferenceId && mpKey ? (
                    <div className="w-full relative [&_button]:!py-4 [&_button]:!rounded-2xl [&_button]:!text-lg [&_button]:!font-bold [&_button]:!bg-[#009EE3] [&_button]:hover:!bg-[#0089C7]">
                      <Wallet initialization={{ preferenceId, redirectMode: "modal" as any }} />
                    </div>
                  ) : preferenceId && !mpKey ? (
                    <div className="w-full text-center p-3 bg-red-50 border border-red-200 text-red-600 rounded">
                      Falta configurar NEXT_PUBLIC_MERCADOPAGO_PUBLIC_KEY
                    </div>
                  ) : (
                    <button 
                      disabled
                      className="w-full py-4 bg-slate-800 text-slate-500 font-bold text-lg rounded-2xl flex justify-center items-center gap-3 cursor-not-allowed"
                    >
                      <Handshake className="w-6 h-6" />
                      No disponible
                    </button>
                  )}
                  <p className="text-center text-[#009EE3] text-xs font-medium mt-3">Paga de forma segura</p>
                  <p className="text-center text-slate-500 text-[10px] mt-4">Procesado por MercadoPago Checkout Pro</p>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      )}
      {/* Success Message Overlay */}
      {successMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#040B16]/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-emerald-500/10 border border-emerald-500/20 p-10 rounded-3xl max-w-lg text-center shadow-[0_0_50px_rgba(16,185,129,0.2)]">
            <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(16,185,129,0.5)]">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-4xl font-black text-white mb-4">¡Pago Aprobado!</h2>
            <p className="text-emerald-400 text-lg mb-2 font-semibold">Tu cita ha sido confirmada con éxito.</p>
            <p className="text-slate-400 text-sm">En 5 segundos serás redirigido al inicio...</p>
          </div>
        </div>
      )}

      {/* Modal de Horario Tomado / en Proceso (409) */}
      {showSlotConflictModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-[#0A1424] border border-amber-500/40 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-[0_0_50px_rgba(245,158,11,0.25)] relative text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
            <h3 className="text-xl font-extrabold text-white mb-2">
              Horario No Disponible o en Verificación
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-6">
              Ese horario acaba de ser tomado o está siendo procesado por otra persona. Puedes elegir otra hora disponible o consultar con nuestra línea logística para validar si la transacción está en curso.
            </p>

            <div className="flex flex-col gap-3">
              <a
                href={`https://wa.me/${logisticsContact.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                  `Hola SYNAPTIX, intenté agendar una ecografía el ${formData.preferred_date || ""} a las ${formData.preferred_time || ""} pero me indica que el horario no está disponible. Quisiera validar con logística si la transacción está en curso o si fue tomado por otra persona.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm rounded-xl transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2"
              >
                <span>Consultar con Logística por WhatsApp</span>
              </a>

              <button
                type="button"
                onClick={() => {
                  setShowSlotConflictModal(false);
                  setShowPaymentModal(false);
                }}
                className="w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm rounded-xl transition-colors border border-slate-700"
              >
                Elegir otra hora disponible
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tiny Footer */}
      <footer className="text-center text-[11px] text-slate-500 z-10 mt-2">
        SYNAPTIX IA HealthTech • Ecografía Veterinaria a Domicilio en Bogotá
      </footer>

      <ModalAlert
        isOpen={modalAlert.isOpen}
        onClose={() => setModalAlert((prev) => ({ ...prev, isOpen: false }))}
        title={modalAlert.title}
        message={modalAlert.message}
        variant={modalAlert.variant}
      />
    </main>
  );
}
